import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { t } from 'services/translation';
import { IntlProvider, useIntl } from 'react-intl';
import { Spinner } from '@anyroad/spinner';
import type { FieldErrors } from 'react-hook-form';
import { brandNeutralColors, colors } from '@anyroad/theme';
import { getDates, getTimesForEachDate } from './TimeDateHelpers';
import {
  DateSelectContainer,
  Label,
  DateBasicSelect,
  TimeSelectContainer,
  TimeBasicSelect,
  SelectWrapper,
  DateTimeContainer,
  SingleAvailableTime,
  SpinnerContainer,
  AddSlot,
  Placeholder,
  DropdownIcon,
  RemoveDateTimeContainer,
  RemoveIcon,
} from './styles';

type SlotContainterProps = {
  errors: FieldErrors<any>;
  experienceId: number;
  handleAddValue: (arg1: number) => void;
  setValue: any;
  brandColor: string;
  isoTime: boolean;
};

type DateSelectionProps = {
  waitlistSlots: any;
  slotId: string;
  selectedDate: Date;
  updateSlot: (arg1: string, arg2: Date, arg3: any) => void;
  brandColor: string;
};

type Slot = {
  id: string;
  slotId: string;
  selectedDate: Date;
  firstSlot?: boolean;
};

export const DateSelection = ({
  waitlistSlots,
  slotId,
  selectedDate,
  updateSlot,
  brandColor,
}: DateSelectionProps) => {
  const handleDateChange = (newSelectedDate: Date) => {
    updateSlot(slotId, newSelectedDate, null);
  };
  return (
    <SelectWrapper>
      <DateBasicSelect
        availableDates={getDates(waitlistSlots)}
        disabled={false}
        onChange={(value: Date) => handleDateChange(value)}
        selected={selectedDate}
        brandColor={brandColor}
      />
    </SelectWrapper>
  );
};

const TimeSelect = ({
  waitlistSlots,
  handleAddValue,
  selectedDate,
  selectSlotId,
  isoTime,
}: any) => {
  const intl = useIntl();
  const formatTime12Hour = (date: Date) =>
    intl.formatTime(date, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: !isoTime,
    });
  const times = useMemo(
    () => getTimesForEachDate(waitlistSlots, formatTime12Hour, intl.formatDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [waitlistSlots]
  );
  const simplifiedDate = intl.formatDate(selectedDate, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });

  const options = times[simplifiedDate] || [];
  const handleTimeSelect = (time: any) => {
    selectSlotId(time.slotId);
    handleAddValue(time.slotId);
  };

  useEffect(() => {
    if (options.length === 1) {
      handleTimeSelect(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selectedDate]);

  const singleTime = options && options.length > 0 && options[0]?.label;
  const DropdownIndicator = () => (
    <DropdownIcon
      className="icon af-triangle-down-2"
      style={{ fontSize: '12px' }}
    />
  );

  return (
    <>
      {options.length === 1 ? (
        <SingleAvailableTime>{singleTime}</SingleAvailableTime>
      ) : (
        <SelectWrapper>
          <TimeBasicSelect
            isSearchable={false}
            components={{
              DropdownIndicator,
            }}
            styles={{
              option: (base: any) => ({
                ...base,
                color: brandNeutralColors.text.primary,
                borderBottom: `1px solid ${colors.border}`,
              }),
              singleValue: (base: any) => ({
                ...base,
              }),
            }}
            onChange={(time: any) => handleTimeSelect(time)}
            options={times[simplifiedDate]}
            placeholder={<Placeholder>{t('SELECT')}</Placeholder>}
          />
        </SelectWrapper>
      )}
    </>
  );
};

export const SlotContainter = ({
  errors,
  experienceId,
  handleAddValue,
  setValue,
  brandColor,
  isoTime,
}: SlotContainterProps) => {
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);
  const [waitlistSlots, setWaitlistSlots] = useState<any>([]);
  const [slots, setSlots] = useState<any>([]);

  useEffect(() => {
    const fetchSlots = async () => {
      setLoadingSlots(true);
      let response: any = await fetch(
        `/experiences/experiences/${experienceId}/waitlist-slots`
      );
      response = await response.json();
      setWaitlistSlots(response.items);
      setLoadingSlots(false);
      if (response.items.length > 1) {
        setSlots([
          { id: uuidv4(), selectedDate: null, slotId: null, firstSlot: true },
        ]);
        setValue('slots', [{ selectedDate: null, slotId: null }]);
      } else {
        const availableDates = getDates(response?.items);
        const slotId = response.items[0].slot_id;
        setSlots([
          { selectedDate: availableDates[0], slotId, firstSlot: true },
        ]);
        setValue('slots', [{ selectedDate: availableDates[0], slotId }]);
      }
    };
    fetchSlots();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  }, [experienceId, setValue]);

  const addSlot = () => {
    const newSlot = { id: uuidv4(), selectedDate: null, slotId: null };
    setSlots([...slots, newSlot]);
    setValue('slots', [...slots, newSlot]);
  };

  const removeSlot = (id: string) => {
    const updatedSlots = slots.filter((slot: Slot) => slot.id !== id);
    setSlots(updatedSlots);
    setValue('slots', updatedSlots);
  };

  const updateSlot = (id: string, selectedDate: Date, slotId: any) => {
    const updatedSlots = slots.map((slot: Slot) => {
      if (slot.id === id) {
        return { ...slot, selectedDate, slotId };
      }
      return slot;
    });
    setSlots(updatedSlots);
    setValue('slots', updatedSlots);
  };

  const noSlotsLeft = waitlistSlots.length === slots.length;
  return loadingSlots ? (
    <SpinnerContainer>
      <Spinner />
    </SpinnerContainer>
  ) : (
    <>
      {slots.map((slot: Slot, index: number) => (
        <DateTimeContainer
          key={slot.id}
          lastSlot={index + 1 === waitlistSlots.length}
        >
          <DateSelectContainer>
            <Label
              showError={Boolean(errors.slots)}
              style={{ display: index > 0 ? 'none' : 'block' }}
            >
              {t('PREFERRED_DATE')}
            </Label>
            <DateSelection
              slotId={slot.id}
              selectedDate={slot.selectedDate}
              updateSlot={updateSlot}
              waitlistSlots={waitlistSlots}
              brandColor={brandColor}
            />
          </DateSelectContainer>
          <IntlProvider defaultLocale="en-US" locale="en-US" messages={{}}>
            <TimeSelectContainer first={slot.firstSlot}>
              <Label
                showError={Boolean(errors.slots)}
                style={{ display: index > 0 ? 'none' : 'block' }}
              >
                {t('START_TIME')}
              </Label>
              <TimeSelect
                handleAddValue={handleAddValue}
                index={index}
                selectSlotId={(slotId: number) =>
                  updateSlot(slot.id, slot.selectedDate, slotId)
                }
                selectedDate={slot.selectedDate}
                waitlistSlots={waitlistSlots}
                isoTime={isoTime}
              />
            </TimeSelectContainer>
            {!slot.firstSlot && (
              <RemoveDateTimeContainer onClick={() => removeSlot(slot.id)}>
                <RemoveIcon fill={colors.text.link} name="ControlRemove" />
              </RemoveDateTimeContainer>
            )}
          </IntlProvider>
        </DateTimeContainer>
      ))}
      {Boolean(!noSlotsLeft) && (
        <AddSlot className="brand-color" onClick={addSlot}>
          {t('ADD_ANOTHER_DATE')}
        </AddSlot>
      )}
    </>
  );
};
